import React, { useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";

import {
  TbEyeSearch,
  TbUserEdit,
  TbCategoryPlus,
} from "react-icons/tb";
import { FiTrash2 } from "react-icons/fi";
import { TiFlowChildren } from "react-icons/ti";

import api from "../api/axios";
import FormViewSidebar from "../components/FormViewSidebar";

import "../assets/styles/page.css";

const PAGE_NAME = "Categories";

const emptyCategory = {
  name: "",
  status: "active",
  image: null,
  remove_image: false,
};

const emptySubCategory = {
  category_id: "",
  name: "",
  status: "active",
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  /*
   * The Axios interceptor opens the global 403 dialog.
   * Avoid showing a duplicate toast.
   */
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message ||
      fallbackMessage
  );
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [categorySearch, setCategorySearch] =
    useState("");
  const [subCategorySearch, setSubCategorySearch] =
    useState("");

  const [selectedCategory, setSelectedCategory] =
    useState(null);

  const [categorySidebar, setCategorySidebar] =
    useState(false);
  const [
    subCategorySidebar,
    setSubCategorySidebar,
  ] = useState(false);

  const [formViewOpen, setFormViewOpen] =
    useState(false);

  const [
    selectedSubCategoryForForm,
    setSelectedSubCategoryForForm,
  ] = useState(null);

  const [
    editingCategoryId,
    setEditingCategoryId,
  ] = useState(null);

  const [
    editingSubCategoryId,
    setEditingSubCategoryId,
  ] = useState(null);

  const [categoryForm, setCategoryForm] =
    useState(emptyCategory);

  const [categoryImagePreview, setCategoryImagePreview] =
    useState(null);

  const [subCategoryForm, setSubCategoryForm] =
    useState(emptySubCategory);

  const [
    loadingCategories,
    setLoadingCategories,
  ] = useState(false);

  const [
    loadingSubCategories,
    setLoadingSubCategories,
  ] = useState(false);

  const [savingCategory, setSavingCategory] =
    useState(false);

  const [
    savingSubCategory,
    setSavingSubCategory,
  ] = useState(false);

  const filteredCategories = useMemo(() => {
    const term = categorySearch
      .trim()
      .toLowerCase();

    if (!term) return categories;

    return categories.filter((category) =>
      category.name
        ?.toLowerCase()
        .includes(term)
    );
  }, [categories, categorySearch]);

  const filteredSubCategories = useMemo(() => {
    const term = subCategorySearch
      .trim()
      .toLowerCase();

    if (!term) return subCategories;

    return subCategories.filter(
      (subCategory) =>
        subCategory.name
          ?.toLowerCase()
          .includes(term)
    );
  }, [subCategories, subCategorySearch]);

  const fetchSubCategoriesByCategory =
    async (categoryId) => {
      if (!categoryId) {
        setSubCategories([]);
        return;
      }

      try {
        setLoadingSubCategories(true);

        const response = await api.get(
          `/admin/products/categories/${categoryId}/sub-categories`,
          {
            headers:
              getPermissionHeaders("view"),
          }
        );

        setSubCategories(
          response.data?.data || []
        );
      } catch (error) {
        setSubCategories([]);

        showRequestError(
          error,
          "Failed to fetch sub-categories."
        );
      } finally {
        setLoadingSubCategories(false);
      }
    };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);

      const response = await api.get(
        "/admin/products/categories",
        {
          headers:
            getPermissionHeaders("view"),
        }
      );

      const data = response.data?.data || [];

      setCategories(data);

      if (
        !selectedCategory &&
        data.length > 0
      ) {
        const firstCategory = data[0];

        setSelectedCategory(firstCategory);

        await fetchSubCategoriesByCategory(
          firstCategory.category_id
        );
      }

      if (data.length === 0) {
        setSelectedCategory(null);
        setSubCategories([]);
      }
    } catch (error) {
      setCategories([]);
      setSelectedCategory(null);
      setSubCategories([]);

      showRequestError(
        error,
        "Failed to fetch categories."
      );
    } finally {
      setLoadingCategories(false);
    }
  };

  const refreshAfterChange = async () => {
    try {
      const response = await api.get(
        "/admin/products/categories",
        {
          headers:
            getPermissionHeaders("view"),
        }
      );

      const updatedCategories =
        response.data?.data || [];

      setCategories(updatedCategories);

      if (updatedCategories.length === 0) {
        setSelectedCategory(null);
        setSubCategories([]);
        return;
      }

      if (selectedCategory) {
        const freshSelected =
          updatedCategories.find(
            (category) =>
              String(category.category_id) ===
              String(
                selectedCategory.category_id
              )
          ) || updatedCategories[0];

        setSelectedCategory(freshSelected);

        await fetchSubCategoriesByCategory(
          freshSelected.category_id
        );

        return;
      }

      const firstCategory =
        updatedCategories[0];

      setSelectedCategory(firstCategory);

      await fetchSubCategoriesByCategory(
        firstCategory.category_id
      );
    } catch (error) {
      showRequestError(
        error,
        "Failed to refresh categories."
      );
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (categoryImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(categoryImagePreview);
      }
    },
    [categoryImagePreview]
  );

  const selectCategory = async (category) => {
    setSelectedCategory(category);
    setSubCategorySearch("");

    await fetchSubCategoriesByCategory(
      category.category_id
    );
  };

  const openCreateCategory = () => {
    setEditingCategoryId(null);
    setCategoryForm(emptyCategory);
    setCategoryImagePreview(null);
    setCategorySidebar(true);
  };

  const openEditCategory = (category) => {
    setEditingCategoryId(
      category.category_id
    );

    setCategoryForm({
      name: category.name || "",
      status:
        category.status || "active",
      image: null,
      remove_image: false,
    });

    setCategoryImagePreview(category.image_url || null);

    setCategorySidebar(true);
  };

  const handleCategoryImageChange = (event) => {
    const image = event.target.files?.[0] || null;

    setCategoryForm((previous) => ({
      ...previous,
      image,
      remove_image: false,
    }));

    setCategoryImagePreview(image ? URL.createObjectURL(image) : null);
  };

  const removeCategoryImage = () => {
    setCategoryForm((previous) => ({
      ...previous,
      image: null,
      remove_image: true,
    }));

    setCategoryImagePreview(null);
  };

  const openCreateSubCategory = () => {
    if (!selectedCategory) {
      toast.error(
        "Select a category first."
      );
      return;
    }

    setEditingSubCategoryId(null);

    setSubCategoryForm({
      ...emptySubCategory,
      category_id:
        selectedCategory.category_id,
    });

    setSubCategorySidebar(true);
  };

  const openEditSubCategory = (
    subCategory
  ) => {
    setEditingSubCategoryId(
      subCategory.sub_category_id
    );

    setSubCategoryForm({
      category_id:
        subCategory.category_id ||
        selectedCategory?.category_id ||
        "",
      name: subCategory.name || "",
      status:
        subCategory.status || "active",
    });

    setSubCategorySidebar(true);
  };

  const openSubCategoryForm = (
    subCategory
  ) => {
    setSelectedSubCategoryForForm(
      subCategory
    );

    setFormViewOpen(true);
  };

  const submitCategory = async (event) => {
    event.preventDefault();

    const cleanName =
      categoryForm.name.trim();

    if (!cleanName) {
      toast.error(
        "Category name is required."
      );
      return;
    }

    try {
      setSavingCategory(true);

      const payload = new FormData();

      payload.append("name", cleanName);
      payload.append("status", categoryForm.status);
      payload.append(
        "remove_image",
        categoryForm.remove_image ? "true" : "false"
      );

      if (categoryForm.image) {
        payload.append("image", categoryForm.image);
      }

      if (editingCategoryId) {
        await api.put(
          `/admin/products/categories/${editingCategoryId}`,
          payload,
          {
            headers:
              getPermissionHeaders("edit"),
          }
        );

        toast.success(
          "Category updated successfully."
        );
      } else {
        await api.post(
          "/admin/products/categories",
          payload,
          {
            headers:
              getPermissionHeaders("add"),
          }
        );

        toast.success(
          "Category created successfully."
        );
      }

      setCategorySidebar(false);
      setEditingCategoryId(null);
      setCategoryForm(emptyCategory);
      setCategoryImagePreview(null);

      await refreshAfterChange();
    } catch (error) {
      showRequestError(
        error,
        "Category save failed."
      );
    } finally {
      setSavingCategory(false);
    }
  };

  const submitSubCategory = async (
    event
  ) => {
    event.preventDefault();

    const cleanName =
      subCategoryForm.name.trim();

    if (
      !subCategoryForm.category_id ||
      !cleanName
    ) {
      toast.error(
        "Parent category and sub-category name are required."
      );
      return;
    }

    try {
      setSavingSubCategory(true);

      const payload = {
        category_id:
          subCategoryForm.category_id,
        name: cleanName,
        status: subCategoryForm.status,
      };

      if (editingSubCategoryId) {
        await api.put(
          `/admin/products/sub-categories/${editingSubCategoryId}`,
          payload,
          {
            headers:
              getPermissionHeaders("edit"),
          }
        );

        toast.success(
          "Sub-category updated successfully."
        );
      } else {
        await api.post(
          "/admin/products/sub-categories",
          payload,
          {
            headers:
              getPermissionHeaders("add"),
          }
        );

        toast.success(
          "Sub-category created successfully."
        );
      }

      setSubCategorySidebar(false);
      setEditingSubCategoryId(null);
      setSubCategoryForm(
        emptySubCategory
      );

      await refreshAfterChange();
    } catch (error) {
      showRequestError(
        error,
        "Sub-category save failed."
      );
    } finally {
      setSavingSubCategory(false);
    }
  };

  const confirmDeleteCategory = (
    category
  ) => {
    confirmDialog({
      message: `Delete "${category.name}" and its linked sub-categories?`,
      header: "Delete Category",
      icon: "pi pi-exclamation-triangle",
      acceptClassName:
        "p-button-danger",

      accept: async () => {
        try {
          await api.delete(
            `/admin/products/categories/${category.category_id}`,
            {
              headers:
                getPermissionHeaders(
                  "delete"
                ),
            }
          );

          toast.success(
            "Category deleted successfully."
          );

          if (
            String(
              selectedCategory?.category_id
            ) ===
            String(category.category_id)
          ) {
            setSelectedCategory(null);
            setSubCategories([]);
          }

          await refreshAfterChange();
        } catch (error) {
          showRequestError(
            error,
            "Category delete failed."
          );
        }
      },
    });
  };

  const confirmDeleteSubCategory = (
    subCategory
  ) => {
    confirmDialog({
      message: `Delete "${subCategory.name}"?`,
      header: "Delete Sub-category",
      icon: "pi pi-exclamation-triangle",
      acceptClassName:
        "p-button-danger",

      accept: async () => {
        try {
          await api.delete(
            `/admin/products/sub-categories/${subCategory.sub_category_id}`,
            {
              headers:
                getPermissionHeaders(
                  "delete"
                ),
            }
          );

          toast.success(
            "Sub-category deleted successfully."
          );

          await refreshAfterChange();
        } catch (error) {
          showRequestError(
            error,
            "Sub-category delete failed."
          );
        }
      },
    });
  };

  return (
    <section className="admin-page categories-page compact-categories-page">
      <ConfirmDialog />

      <div className="page-header compact-page-header">
        <div>
          <p>Collections</p>
          <h1>Categories</h1>
        </div>

        <div className="page-actions">
          <button
            type="button"
            className="ghost-action"
            data-tooltip-id="subTip"
            data-tooltip-content="Add Sub-category"
            onClick={openCreateSubCategory}
            disabled={!selectedCategory}
          >
            <TiFlowChildren />
          </button>

          <button
            type="button"
            className="ghost-action"
            data-tooltip-id="addTip"
            data-tooltip-content="Add Category"
            onClick={openCreateCategory}
          >
            <TbCategoryPlus />
          </button>

          <Tooltip id="subTip" />
          <Tooltip id="addTip" />
        </div>
      </div>

      <div className="category-master-layout">
        <div className="category-master-panel">
          <div className="compact-panel-header">
            <div>
              <h2>Categories</h2>
              <p>
                {categories.length} total
              </p>
            </div>

            <input
              value={categorySearch}
              onChange={(event) =>
                setCategorySearch(
                  event.target.value
                )
              }
              placeholder="Search..."
            />
          </div>

          <div className="category-master-list">
            {loadingCategories && (
              <div className="mini-empty">
                Loading...
              </div>
            )}

            {!loadingCategories &&
              filteredCategories.length ===
                0 && (
                <div className="mini-empty">
                  No categories found.
                </div>
              )}

            {filteredCategories.map(
              (category) => {
                const isActive =
                  String(
                    selectedCategory?.category_id
                  ) ===
                  String(
                    category.category_id
                  );

                return (
                  <button
                    type="button"
                    key={
                      category.category_id
                    }
                    className={`category-master-item ${
                      isActive
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      selectCategory(
                        category
                      )
                    }
                  >
                    <span className="category-master-icon">
                      {category.image_url ? (
                        <img
                          src={category.image_url}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        category.name
                          ?.charAt(0)
                          ?.toUpperCase() || "C"
                      )}
                    </span>

                    <span className="category-master-info">
                      <strong>
                        {category.name}
                      </strong>

                      <small>
                        {category
                          .sub_categories
                          ?.length || 0}{" "}
                        sub-categories
                      </small>
                    </span>

                    <span
                      className={`mini-status ${category.status}`}
                    >
                      {category.status}
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </div>

        <div className="subcategory-detail-panel">
          <div className="compact-panel-header">
            <div>
              <h2>
                {selectedCategory?.name ||
                  "Select Category"}
              </h2>

              <p>
                {selectedCategory
                  ? `${filteredSubCategories.length} sub-categories shown`
                  : "Choose a category to view sub-categories"}
              </p>
            </div>

            <input
              value={subCategorySearch}
              onChange={(event) =>
                setSubCategorySearch(
                  event.target.value
                )
              }
              placeholder="Search sub-categories..."
              disabled={!selectedCategory}
            />
          </div>

          {selectedCategory && (
            <div className="selected-category-toolbar">
              <div>
                <span
                  className={`status-badge ${selectedCategory.status}`}
                >
                  {selectedCategory.status}
                </span>

                <code className="inline-code">
                  category slug:{" "}
                  {selectedCategory.slug}
                </code>
              </div>

              <div>
                <button
                  type="button"
                  className="ghost-action"
                  data-tooltip-id="categoryEditTip"
                  data-tooltip-content="Edit Category"
                  onClick={() =>
                    openEditCategory(
                      selectedCategory
                    )
                  }
                >
                  <TbUserEdit />
                </button>

                <button
                  type="button"
                  className="ghost-action danger-action"
                  data-tooltip-id="categoryDeleteTip"
                  data-tooltip-content="Delete Category"
                  onClick={() =>
                    confirmDeleteCategory(
                      selectedCategory
                    )
                  }
                >
                  <FiTrash2 />
                </button>

                <Tooltip id="categoryEditTip" />
                <Tooltip id="categoryDeleteTip" />
              </div>
            </div>
          )}

          <div className="subcategory-compact-grid">
            {loadingSubCategories && (
              <div className="mini-empty">
                Loading...
              </div>
            )}

            {!loadingSubCategories &&
              selectedCategory &&
              filteredSubCategories.length ===
                0 && (
                <div className="mini-empty">
                  No sub-categories found for
                  this category.
                </div>
              )}

            {!selectedCategory && (
              <div className="mini-empty">
                Select a category from the
                left panel.
              </div>
            )}

            {filteredSubCategories.map(
              (subCategory) => (
                <div
                  className="subcategory-compact-card"
                  key={
                    subCategory.sub_category_id
                  }
                >
                  <div>
                    <h3>
                      {subCategory.name}
                    </h3>

                    <p>
                      {subCategory.slug}
                    </p>
                  </div>

                  <div className="sub-card-meta">
                    <span
                      className={`status-badge ${subCategory.status}`}
                    >
                      {subCategory.status}
                    </span>
                  </div>

                  <div className="sub-card-actions category-form-actions">
                    <div className="sub-card-actions text-end">
                      <button
                        type="button"
                        className="ghost-action"
                        data-tooltip-id={`formViewTip-${subCategory.sub_category_id}`}
                        data-tooltip-content="View Form"
                        onClick={() =>
                          openSubCategoryForm(
                            subCategory
                          )
                        }
                      >
                        <TbEyeSearch />
                      </button>

                      <button
                        type="button"
                        className="ghost-action"
                        data-tooltip-id={`subEditTip-${subCategory.sub_category_id}`}
                        data-tooltip-content="Edit"
                        onClick={() =>
                          openEditSubCategory(
                            subCategory
                          )
                        }
                      >
                        <TbUserEdit />
                      </button>

                      <button
                        type="button"
                        className="ghost-action danger-action"
                        data-tooltip-id={`subDeleteTip-${subCategory.sub_category_id}`}
                        data-tooltip-content="Delete"
                        onClick={() =>
                          confirmDeleteSubCategory(
                            subCategory
                          )
                        }
                      >
                        <FiTrash2 />
                      </button>
                    </div>

                    <Tooltip
                      id={`formViewTip-${subCategory.sub_category_id}`}
                    />

                    <Tooltip
                      id={`subEditTip-${subCategory.sub_category_id}`}
                    />

                    <Tooltip
                      id={`subDeleteTip-${subCategory.sub_category_id}`}
                    />
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <Sidebar
        visible={categorySidebar}
        position="right"
        onHide={() =>
          setCategorySidebar(false)
        }
        className="premium-crud-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Collection Setup</p>

            <h2>
              {editingCategoryId
                ? "Edit Category"
                : "Add Category"}
            </h2>

            <span>
              {editingCategoryId
                ? "Update this main collection."
                : "Create a new main product collection."}
            </span>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={submitCategory}
          >
            <label>
              Category Name

              <input
                value={categoryForm.name}
                onChange={(event) =>
                  setCategoryForm(
                    (previous) => ({
                      ...previous,
                      name:
                        event.target.value,
                    })
                  )
                }
                placeholder="Wedding Invitations"
                required
              />
            </label>

            <label>
              Homepage Image

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCategoryImageChange}
              />

              <small className="field-help">
                Used in the storefront's Shop by Category section.
              </small>
            </label>

            {categoryImagePreview && (
              <div className="category-image-preview">
                <img
                  src={categoryImagePreview}
                  alt="Category preview"
                />

                <button
                  type="button"
                  className="ghost-action danger-action"
                  onClick={removeCategoryImage}
                >
                  Remove image
                </button>
              </div>
            )}

            <label>
              Status

              <select
                value={
                  categoryForm.status
                }
                onChange={(event) =>
                  setCategoryForm(
                    (previous) => ({
                      ...previous,
                      status:
                        event.target.value,
                    })
                  )
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="inactive">
                  Inactive
                </option>
              </select>
            </label>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() =>
                  setCategorySidebar(false)
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  savingCategory
                }
              >
                {savingCategory
                  ? "Saving..."
                  : editingCategoryId
                    ? "Update Category"
                    : "Create Category"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>

      <Sidebar
        visible={subCategorySidebar}
        position="right"
        onHide={() =>
          setSubCategorySidebar(false)
        }
        className="premium-crud-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Collection Setup</p>

            <h2>
              {editingSubCategoryId
                ? "Edit Sub-category"
                : "Add Sub-category"}
            </h2>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={
              submitSubCategory
            }
          >
            <label>
              Parent Category

              <select
                value={
                  subCategoryForm.category_id
                }
                onChange={(event) =>
                  setSubCategoryForm(
                    (previous) => ({
                      ...previous,
                      category_id:
                        event.target.value,
                    })
                  )
                }
                required
              >
                <option value="">
                  Select category
                </option>

                {categories.map(
                  (category) => (
                    <option
                      key={
                        category.category_id
                      }
                      value={
                        category.category_id
                      }
                    >
                      {category.name}
                    </option>
                  )
                )}
              </select>
            </label>

            <label>
              Sub-category Name

              <input
                value={
                  subCategoryForm.name
                }
                onChange={(event) =>
                  setSubCategoryForm(
                    (previous) => ({
                      ...previous,
                      name:
                        event.target.value,
                    })
                  )
                }
                placeholder="Acrylic Invites"
                required
              />
            </label>

            <label>
              Status

              <select
                value={
                  subCategoryForm.status
                }
                onChange={(event) =>
                  setSubCategoryForm(
                    (previous) => ({
                      ...previous,
                      status:
                        event.target.value,
                    })
                  )
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="inactive">
                  Inactive
                </option>
              </select>
            </label>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() =>
                  setSubCategorySidebar(false)
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  savingSubCategory
                }
              >
                {savingSubCategory
                  ? "Saving..."
                  : editingSubCategoryId
                    ? "Update Sub-category"
                    : "Create Sub-category"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>

      <FormViewSidebar
        visible={formViewOpen}
        onHide={() =>
          setFormViewOpen(false)
        }
        subCategoryId={
          selectedSubCategoryForForm
            ?.sub_category_id
        }
        subCategoryName={
          selectedSubCategoryForForm?.name
        }
      />
    </section>
  );
};

export default Categories;
